import { saveAs } from "file-saver";

// FEC récupéré depuis ACD, téléchargé en Excel lisible (demande de Jonathan du
// 20/10/2026) : le fichier officiel (texte tabulé, Windows-1252, dates
// AAAAMMJJ, montants « 1234,56 ») est converti dans le navigateur, au clic.
// Le FEC stocké pour l'analyse du CRM n'est pas modifié.
// exceljs est chargé à la demande (voir Portefeuille/ExportToExcel.js).

const BORDEAUX = "FF840040";
const BANDE = "FFFAF5F7";

// Colonnes officielles du FEC (article A47 A-1 du LPF) : libellé lisible,
// type et largeur. Une colonne inconnue garde son nom, en texte.
const COLONNES = {
  JournalCode: { libelle: "Code journal", largeur: 10 },
  JournalLib: { libelle: "Journal", largeur: 24 },
  EcritureNum: { libelle: "N° écriture", largeur: 12 },
  EcritureDate: { libelle: "Date", type: "date", largeur: 12 },
  CompteNum: { libelle: "Compte", largeur: 12 },
  CompteLib: { libelle: "Libellé du compte", largeur: 30 },
  CompAuxNum: { libelle: "Compte auxiliaire", largeur: 14 },
  CompAuxLib: { libelle: "Libellé auxiliaire", largeur: 28 },
  PieceRef: { libelle: "Pièce", largeur: 16 },
  PieceDate: { libelle: "Date pièce", type: "date", largeur: 12 },
  EcritureLib: { libelle: "Libellé de l'écriture", largeur: 42 },
  Debit: { libelle: "Débit", type: "montant", largeur: 14 },
  Credit: { libelle: "Crédit", type: "montant", largeur: 14 },
  Montant: { libelle: "Montant", type: "montant", largeur: 14 },
  Sens: { libelle: "Sens", largeur: 6 },
  EcritureLet: { libelle: "Lettrage", largeur: 9 },
  DateLet: { libelle: "Date lettrage", type: "date", largeur: 12 },
  ValidDate: { libelle: "Date validation", type: "date", largeur: 12 },
  Montantdevise: { libelle: "Montant devise", type: "montant", largeur: 14 },
  Idevise: { libelle: "Devise", largeur: 7 },
};

// UTF-8 si le fichier en est, sinon Windows-1252 (cas d'ACD).
function decoder(buffer) {
  try {
    return new TextDecoder("utf-8", { fatal: true }).decode(buffer);
  } catch {
    return new TextDecoder("windows-1252").decode(buffer);
  }
}

// « 20240701 » (ou « 01/07/2024 ») → date UTC, pour qu'Excel affiche le jour exact.
function date(v) {
  const s = String(v || "").trim();
  let m = s.match(/^(\d{4})(\d{2})(\d{2})$/);
  if (m) return new Date(Date.UTC(+m[1], +m[2] - 1, +m[3]));
  m = s.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (m) return new Date(Date.UTC(+m[3], +m[2] - 1, +m[1]));
  return s || null;
}

// « 1 234,56 » → 1234.56
function montant(v) {
  const s = String(v || "").replace(/\s/g, "").replace(",", ".");
  if (!s) return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : v;
}

const dateFr = (iso) => (iso ? iso.split("-").reverse().join("/") : "");
const lettreColonne = (n) => {
  let s = "";
  for (let i = n; i > 0; i = Math.floor((i - 1) / 26)) s = String.fromCharCode(65 + ((i - 1) % 26)) + s;
  return s;
};

/**
 * Télécharge le FEC ACD en .xlsx.
 * file : { url, nom, exercice: { debut, fin }, lignes } (réponse du backend).
 * label : « FEC N » / « FEC N-1 ».
 */
export async function telechargerFecExcel(file, label) {
  const res = await fetch(file.url);
  if (!res.ok) throw new Error(`Fichier introuvable (${res.status}).`);
  const lignes = decoder(await res.arrayBuffer())
    .split(/\r?\n/)
    .filter((l) => l.trim());
  if (!lignes.length) throw new Error("Le fichier est vide.");

  const separateur = lignes[0].includes("\t") ? "\t" : "|";
  const entetes = lignes[0].split(separateur).map((h) => h.trim());
  const defs = entetes.map((h) => ({ cle: h, ...(COLONNES[h] || { libelle: h, largeur: 14 }) }));

  const mod = await import("exceljs");
  const ExcelJS = mod.default || mod;
  const wb = new ExcelJS.Workbook();
  wb.creator = "MyALFA";
  const ws = wb.addWorksheet(label || "FEC", {
    views: [{ state: "frozen", ySplit: 4, showGridLines: false }],
    pageSetup: { orientation: "landscape", fitToPage: true, fitToWidth: 1, fitToHeight: 0 },
  });
  // Police et formats posés sur les colonnes avant les lignes : chaque cellule
  // en hérite à sa création (bien plus rapide que de les reprendre après).
  const police = { name: "Calibri", size: 10 };
  ws.columns = defs.map((d) => ({
    width: d.largeur,
    style: {
      font: police,
      ...(d.type === "date" && { numFmt: "dd/mm/yyyy" }),
      ...(d.type === "montant" && { numFmt: '#,##0.00;-#,##0.00;"-"' }),
    },
  }));
  const nbCol = defs.length;
  const derniere = lettreColonne(nbCol);

  // En-tête du document.
  ws.mergeCells(`A1:${derniere}1`);
  ws.getCell("A1").value = `Fichier des écritures comptables — ${label || "FEC"}`;
  ws.getCell("A1").font = { name: "Calibri", size: 15, bold: true, color: { argb: BORDEAUX } };
  ws.getRow(1).height = 24;
  ws.mergeCells(`A2:${derniere}2`);
  ws.getCell("A2").value =
    `Exercice du ${dateFr(file.exercice?.debut)} au ${dateFr(file.exercice?.fin)} · ` +
    `${(lignes.length - 1).toLocaleString("fr-FR")} lignes d'écritures · récupéré depuis ACD · fichier ${file.nom}`;
  ws.getCell("A2").font = { ...police, color: { argb: "FF6B6B6B" } };

  // Ligne de titres des colonnes.
  const titres = ws.getRow(4);
  titres.values = defs.map((d) => d.libelle);
  titres.height = 22;
  titres.eachCell((c, i) => {
    c.font = { ...police, bold: true, color: { argb: "FFFFFFFF" } };
    c.fill = { type: "pattern", pattern: "solid", fgColor: { argb: BORDEAUX } };
    c.alignment = { vertical: "middle", horizontal: defs[i - 1].type === "montant" ? "right" : "left", wrapText: true };
  });

  // Écritures.
  const totaux = {};
  lignes.slice(1).forEach((l, n) => {
    const cellules = l.split(separateur);
    const valeurs = defs.map((d, i) => {
      const v = cellules[i] ?? "";
      if (d.type === "date") return date(v);
      if (d.type === "montant") {
        const m = montant(v);
        if (typeof m === "number") totaux[d.cle] = (totaux[d.cle] || 0) + m;
        return m;
      }
      return String(v).trim() || null;
    });
    const row = ws.addRow(valeurs);
    if (n % 2) row.fill = { type: "pattern", pattern: "solid", fgColor: { argb: BANDE } };
  });

  // Ligne de total (débit, crédit), en formules.
  const debut = 5;
  const fin = ws.rowCount;
  const total = ws.addRow([]);
  defs.forEach((d, i) => {
    if (d.cle !== "Debit" && d.cle !== "Credit" && d.cle !== "Montant") return;
    const lettre = lettreColonne(i + 1);
    total.getCell(i + 1).value = { formula: `SUM(${lettre}${debut}:${lettre}${fin})`, result: Math.round((totaux[d.cle] || 0) * 100) / 100 };
  });
  const premiereMontant = defs.findIndex((d) => d.type === "montant");
  if (premiereMontant > 0) total.getCell(premiereMontant).value = "Total";
  total.eachCell({ includeEmpty: true }, (c, i) => {
    if (i > nbCol) return;
    c.font = { ...police, bold: true };
    c.border = { top: { style: "medium", color: { argb: BORDEAUX } } };
    if (c.value === "Total") c.alignment = { horizontal: "right" };
  });

  // Filtres sur les titres de colonnes.
  ws.autoFilter = { from: { row: 4, column: 1 }, to: { row: fin, column: nbCol } };

  const buffer = await wb.xlsx.writeBuffer();
  saveAs(
    new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }),
    String(file.nom || "FEC.txt").replace(/\.txt$/i, "") + ".xlsx",
  );
}
